/**
 * Which 3D mesh a building is drawn as.
 *
 * THE MESH ENCODES VARIETY, AND EXACTLY ONE FACT. Rendered side by side, the
 * eight KayKit buildings are generic city blocks: three (A, E, G) have
 * ground-floor shop awnings, the rest are plain and differ mainly in height,
 * and **none of them reads as a hospital or a police station**. An earlier
 * mapping handed `building_G` to police and government — a mesh with a
 * shopfront on it. So the only claim a shape makes here is the true one: a
 * shopfront means somewhere you walk into off the street. Everything else is a
 * block, varied so a street does not look stamped from one mould.
 *
 * What a building actually IS lives where it can be read: the badge (mine, has
 * work), the tint (faction, status), the printed name, and the dossier.
 *
 * THE PICK MUST BE DETERMINISTIC PER BUILDING, which is why it hashes the id
 * rather than counting or randomising. A building has to look the same on every
 * reload and to every viewer — and the failure mode of getting this wrong is
 * invisible to a single client, which is exactly how it would ship. The Unity
 * side states the same rule for crew names: FNV-1a, never `Math.random`, never
 * a runtime-seeded hash.
 *
 * A MESH ID THE STYLE DOES NOT DECLARE DRAWS NOTHING — the building vanishes
 * with only a 404, measured in `Docs/graphics.md`. `game_type` comes from the
 * operator-editable `mapbox_category_map` (MafiaGo-API migration 053) with a
 * configurable `fallback_game_type`, so an unknown type is a real possibility
 * and the mapping is total by construction.
 */

/** Every mesh the style declares, and the only values `model-id` may take. */
export const BUILDING_MESHES = [
  'shopfront-1',
  'shopfront-2',
  'shopfront-3',
  'block-1',
  'block-2',
  'block-3',
  'block-4',
  'safehouse',
] as const

export type BuildingMesh = (typeof BUILDING_MESHES)[number]

/**
 * What a `game_type` is, as far as the art is concerned. Three of these share
 * one pool today because the pack has no mesh that reads as a hospital or a
 * police station — the categories stay because this is where better art
 * attaches, not because the meshes currently tell them apart.
 */
export const BUILDING_CATEGORIES = ['retail', 'commercial', 'health', 'civic', 'safehouse'] as const

export type BuildingCategory = (typeof BUILDING_CATEGORIES)[number]

/** Anything unrecognised reads as a storefront: the commonest thing on a street. */
export const FALLBACK_CATEGORY: BuildingCategory = 'retail'

/** The plain blocks. One pool, shared, rather than three arbitrary assignments. */
const BLOCKS = ['block-1', 'block-2', 'block-3', 'block-4'] as const

/** Category → the meshes it may be drawn as. */
const MESH_POOL: Readonly<Record<BuildingCategory, readonly BuildingMesh[]>> = {
  retail: ['shopfront-1', 'shopfront-2', 'shopfront-3'],
  commercial: BLOCKS,
  health: BLOCKS,
  civic: BLOCKS,
  // Fixed, and deliberately not varied: a player should recognise their own
  // safehouse on sight, which a building that might be any of four cannot do.
  safehouse: ['safehouse'],
}

/**
 * Every `game_type` the API seeds (migration 053), plus the player-built
 * `safehouse`. A type missing here still renders, as the fallback, which is a
 * quieter failure than vanishing but is still the wrong building.
 */
const BY_GAME_TYPE: Readonly<Record<string, BuildingCategory>> = {
  // Somewhere you walk into off the street.
  restaurant: 'retail',
  bar: 'retail',
  cafe: 'retail',
  storefront: 'retail',
  // Bigger commercial premises.
  bank: 'commercial',
  shop: 'commercial',
  hotel: 'commercial',
  garage: 'commercial',
  warehouse: 'commercial',
  // Health.
  hospital: 'health',
  pharmacy: 'health',
  // Civic. The phone renders NEITHER of these: `GameTypeClassifier.cs` maps no
  // police/government/warehouse, so they resolve to Unknown and never spawn,
  // and its PolicePrefab slot is wired to a prefab nothing can reach. The web
  // covers them on purpose — a God View with holes where the police stations
  // should be is worse than one showing a building the phone does not.
  police: 'civic',
  government: 'civic',
  // Player-built.
  safehouse: 'safehouse',
}

/** Total: every input, including an empty or unknown one, yields a category. */
export function categoryForGameType(gameType: string | undefined | null): BuildingCategory {
  if (!gameType) return FALLBACK_CATEGORY
  return BY_GAME_TYPE[gameType.trim().toLowerCase()] ?? FALLBACK_CATEGORY
}

/**
 * FNV-1a over the building id.
 *
 * Not `Math.random`, and not a hash whose seed can vary between runs: the same
 * building must be the same mesh on every reload and for every viewer. Reads
 * UTF-16 code units, which is exact for the ids the server issues (UUIDs and
 * `b-001`-style fixtures) and merely stable for anything else.
 */
export function fnv1a(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    // imul, because the 32-bit FNV prime overflows a double.
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** The mesh for one building. Total: always a mesh the style declares. */
export function meshForBuilding(gameType: string | undefined | null, buildingId: string): BuildingMesh {
  const pool = MESH_POOL[categoryForGameType(gameType)]
  return pool[fnv1a(buildingId) % pool.length]
}

/** The game types this bundle knows about, for tests and the Backroom gap check. */
export const KNOWN_GAME_TYPES = Object.freeze(Object.keys(BY_GAME_TYPE))

/** Every mesh any category can produce — must equal what the style declares. */
export const REACHABLE_MESHES = Object.freeze([...new Set(Object.values(MESH_POOL).flat())].sort())
